import { useBlockedTimesQuery, useBookingsQuery } from '../queries/useAppointmentsQueries';
import { convertToAppointments, groupAppointmentsByDate } from '../logic/appointmentsTransform';
import { getLocalTimezone } from '@/lib';
import { endOfMonth, isWithinInterval } from 'date-fns';
import { parseLocalDate } from '@/lib/utils/timeUtils';

interface UseAppointmentsParams {
    timezone?: string;
    startDate?: string;
    endDate?: string;
    bookingStatus?: number;
    page?: number;
    pageSize?: number;
}

/**
 * Hook to fetch and transform appointments data (both blocked times and bookings)
 * Orchestrates queries and transforms data for consumption
 */
export function useAppointments(params: UseAppointmentsParams = {}) {
    const { timezone = getLocalTimezone(), startDate, endDate, bookingStatus, page = 1, pageSize = 10 } = params;

    // Fetch blocked times
    const blockedTimesQuery = useBlockedTimesQuery({ timezone, startDate, endDate });

    // Fetch bookings
    const bookingsQuery = useBookingsQuery({ timezone, startDate, endDate, bookingStatus, page, pageSize });

    // Transform and merge the data
    const allAppointments = convertToAppointments(blockedTimesQuery.data?.data || [], bookingsQuery.data?.data || []);

    // Filter to only show appointments within the actual month (not the extended range)
    // We fetch until the 1st of next month, but only display the current month (like Vue)
    const appointments =
        startDate && endDate
            ? allAppointments.filter(apt => {
                  const aptDate = parseLocalDate(apt.date);
                  const monthStart = parseLocalDate(startDate);
                  const monthEnd = endOfMonth(monthStart);
                  return isWithinInterval(aptDate, { start: monthStart, end: monthEnd });
              })
            : allAppointments;

    const groupedByDate = groupAppointmentsByDate(appointments);

    return {
        appointments,
        groupedByDate,
        isLoading: blockedTimesQuery.isLoading || bookingsQuery.isLoading,
        isError: blockedTimesQuery.isError || bookingsQuery.isError,
        error: blockedTimesQuery.error || bookingsQuery.error,
        pagination: bookingsQuery.data?.metadata,
        refetch: () => {
            blockedTimesQuery.refetch();
            bookingsQuery.refetch();
        },
    };
}
